import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../appUtils/axiosConfig";

/*
|--------------------------------------------------------------------------
| GET ALL BLOGS
|--------------------------------------------------------------------------
| Example:
| GET blogs/get_blogs.php?page=1&limit=9&search=ayurveda
|
*/

export const getBlogs = createAsyncThunk(
  "blogs/getBlogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get("blogs/get_blogs.php", {
        params,
      });

      if (res?.data?.status === true) {
        return res.data;
      }

      return rejectWithValue(
        res?.data?.message || "Failed to fetch blogs"
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch blogs"
      );
    }
  }
);

/*
|--------------------------------------------------------------------------
| GET SINGLE BLOG
|--------------------------------------------------------------------------
| Example:
| GET blogs/get_blog.php?slug=benefits-of-ayurveda
|
*/

export const getBlogDetails = createAsyncThunk(
  "blogs/getBlogDetails",
  async (slug, { rejectWithValue }) => {
    try {
      if (!slug) {
        return rejectWithValue("Blog slug is required");
      }

      const res = await axios.get("blogs/get_blog.php", {
        params: {
          slug,
        },
      });

      if (res?.data?.status === true) {
        return res.data;
      }

      return rejectWithValue(
        res?.data?.message || "Failed to fetch blog"
      );
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch blog"
      );
    }
  }
);

/*
|--------------------------------------------------------------------------
| BLOG SLICE
|--------------------------------------------------------------------------
*/

const blogsSlice = createSlice({
  name: "blogs",

  initialState: {
    list: [],
    total: 0,

    page: 1,
    perPage: 10,
    totalPages: 0,

    activeBlog: null,
    relatedBlogs: [],

    listStatus: "idle",
    detailsStatus: "idle",

    error: null,
  },

  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },

    clearActiveBlog: (state) => {
      state.activeBlog = null;
      state.relatedBlogs = [];
      state.detailsStatus = "idle";
    },

    clearBlogsError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /*
      |--------------------------------------------------------------------------
      | GET BLOGS - PENDING
      |--------------------------------------------------------------------------
      */

      .addCase(getBlogs.pending, (state) => {
        state.listStatus = "loading";
        state.error = null;
      })

      /*
      |--------------------------------------------------------------------------
      | GET BLOGS - SUCCESS
      |--------------------------------------------------------------------------
      */

      .addCase(getBlogs.fulfilled, (state, action) => {
        state.listStatus = "succeeded";
        state.error = null;

        const response = action.payload;

        /*
        API response:
        {
          status: true,
          message: "...",
          data: [...],
          pagination: {
            page: 1,
            limit: 10,
            total: 20,
            totalPages: 2
          }
        }
        */

        state.list = Array.isArray(response?.data)
          ? response.data
          : [];

        state.total = Number(
          response?.pagination?.total ?? state.list.length
        );

        state.page = Number(
          response?.pagination?.page ?? state.page
        );

        state.perPage = Number(
          response?.pagination?.limit ?? state.perPage
        );

        state.totalPages = Number(
          response?.pagination?.totalPages ??
            Math.ceil(state.total / state.perPage)
        );
      })

      /*
      |--------------------------------------------------------------------------
      | GET BLOGS - FAILED
      |--------------------------------------------------------------------------
      */

      .addCase(getBlogs.rejected, (state, action) => {
        state.listStatus = "failed";

        state.list = [];
        state.total = 0;
        state.totalPages = 0;

        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch blogs";
      })

      /*
      |--------------------------------------------------------------------------
      | GET BLOG DETAILS - PENDING
      |--------------------------------------------------------------------------
      */

      .addCase(getBlogDetails.pending, (state) => {
        state.detailsStatus = "loading";
        state.error = null;

        state.activeBlog = null;
        state.relatedBlogs = [];
      })

      /*
      |--------------------------------------------------------------------------
      | GET BLOG DETAILS - SUCCESS
      |--------------------------------------------------------------------------
      */

      .addCase(getBlogDetails.fulfilled, (state, action) => {
        state.detailsStatus = "succeeded";
        state.error = null;

        const response = action.payload;

        /*
        API response:

        {
          status: true,
          message: "...",
          data: {
            blog: {...},
            relatedBlogs: [...]
          }
        }
        */

        state.activeBlog = response?.data?.blog || null;

        state.relatedBlogs = Array.isArray(
          response?.data?.relatedBlogs
        )
          ? response.data.relatedBlogs
          : [];
      })

      /*
      |--------------------------------------------------------------------------
      | GET BLOG DETAILS - FAILED
      |--------------------------------------------------------------------------
      */

      .addCase(getBlogDetails.rejected, (state, action) => {
        state.detailsStatus = "failed";

        state.activeBlog = null;
        state.relatedBlogs = [];

        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch blog";
      });
  },
});

export const {
  setPage,
  clearActiveBlog,
  clearBlogsError,
} = blogsSlice.actions;

export default blogsSlice.reducer;